// Housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md §2): bed cleaning and terminal
// cleans, the gate on giving a patient a bed that is waiting to be cleaned, overdue tasks and
// daily schedules. Pure: callers pass the time.
//
//   a patient leaves ──▶ pending ("needs cleaning") ──start──▶ in_progress ("being cleaned")
//                          │                                   │
//                          └──────────────complete─────────────┴──▶ done (bed free again)
//   open ──skip (scheduled tasks, reason)──▶ skipped
//   open ──override (bed taken before it was cleaned, reason; routine cleans only)──▶ overridden

import { err, ok, type Result } from '../result.ts';
import type { BedBoardState } from '../inpatient/beds.ts';
import { checkClockTime, shiftWindow } from './roster.ts';

export const HOUSEKEEPING_TASK_KINDS = ['scheduled', 'bed_clean', 'terminal_clean'] as const;
export type HousekeepingTaskKind = (typeof HOUSEKEEPING_TASK_KINDS)[number];
export type BedCleaningKind = Exclude<HousekeepingTaskKind, 'scheduled'>;

export const HOUSEKEEPING_TASK_KIND_LABELS: Record<HousekeepingTaskKind, string> = {
  scheduled: 'Scheduled',
  bed_clean: 'Bed clean',
  terminal_clean: 'Terminal clean',
};

export const HOUSEKEEPING_TASK_STATUSES = [
  'pending',
  'in_progress',
  'done',
  'skipped',
  'overridden',
] as const;
export type HousekeepingTaskStatus = (typeof HOUSEKEEPING_TASK_STATUSES)[number];

export const HOUSEKEEPING_TASK_STATUS_LABELS: Record<HousekeepingTaskStatus, string> = {
  pending: 'To do',
  in_progress: 'In progress',
  done: 'Done',
  skipped: 'Skipped',
  overridden: 'Bed used before cleaning',
};

export const HOUSEKEEPING_TASK_SOURCES = [
  'schedule',
  'discharge',
  'transfer',
  'admission_cancelled',
  'bed_board',
] as const;
export type HousekeepingTaskSource = (typeof HOUSEKEEPING_TASK_SOURCES)[number];

/** A task not done this long after its due time is overdue (plan H2, provisional). */
export const OVERDUE_AFTER_MINUTES = 60;
export const HOUSEKEEPING_NOTE_MAX_LENGTH = 500;
export const MAX_SCHEDULE_TIMES = 12;

const OPEN: ReadonlySet<HousekeepingTaskStatus> = new Set(['pending', 'in_progress']);

export function isOpenTask(status: HousekeepingTaskStatus): boolean {
  return OPEN.has(status);
}

/** A terminal clean when the patient leaving had any precaution showing (plan H1). */
export function cleaningKindFor(precautions: readonly string[]): BedCleaningKind {
  return precautions.length > 0 ? 'terminal_clean' : 'bed_clean';
}

export type HousekeepingErrorCode =
  'housekeeping.task_closed' | 'housekeeping.task_started' | 'housekeeping.times_invalid';

export const HOUSEKEEPING_ERROR_MESSAGES: Record<HousekeepingErrorCode, string> = {
  'housekeeping.task_closed': 'This task is already closed. Reload to see who closed it.',
  'housekeeping.task_started': 'This task is already in progress.',
  'housekeeping.times_invalid': `Give one to ${String(MAX_SCHEDULE_TIMES)} times on the 24-hour clock, like 07:00 or 19:30.`,
};

export type TaskAction = 'start' | 'complete' | 'skip' | 'override';

export function taskTransition(
  from: HousekeepingTaskStatus,
  action: TaskAction,
): Result<HousekeepingTaskStatus, HousekeepingErrorCode> {
  if (!isOpenTask(from)) return err('housekeeping.task_closed');
  switch (action) {
    case 'start':
      return from === 'pending' ? ok('in_progress') : err('housekeeping.task_started');
    case 'complete':
      return ok('done');
    case 'skip':
      return ok('skipped');
    case 'override':
      return ok('overridden');
  }
}

export type BedGateErrorCode =
  'bed.needs_cleaning' | 'bed.needs_terminal_clean' | 'bed.not_available';

/**
 * Whether a bed may be given to a patient. A free bed is taken. A bed waiting for a routine
 * clean is taken only with a reason (plan H3), which closes its task as overridden; a bed
 * waiting for a terminal clean never is (plan H4).
 */
export function bedGate(input: {
  state: BedBoardState;
  /** The open cleaning task's kind, or null when the bed has none. */
  cleaningKind: BedCleaningKind | null;
  overrideReason: string | undefined;
}): Result<'take' | 'override', BedGateErrorCode> {
  if (input.state === 'free') return ok('take');
  if (input.state !== 'cleaning') return err('bed.not_available');
  if (input.cleaningKind === 'terminal_clean') return err('bed.needs_terminal_clean');
  if ((input.overrideReason ?? '').trim() === '') return err('bed.needs_cleaning');
  return ok('override');
}

/** Open and more than an hour past its due time (plan H2). */
export function isOverdue(
  task: { status: HousekeepingTaskStatus; dueAt: Date },
  now: Date,
): boolean {
  if (!isOpenTask(task.status)) return false;
  return now.getTime() > task.dueAt.getTime() + OVERDUE_AFTER_MINUTES * 60_000;
}

/** A schedule's times of day: 1 to 12, `HH:MM` on the 24-hour clock, sorted, each once. */
export function checkScheduleTimes(
  times: readonly string[],
): Result<string[], HousekeepingErrorCode> {
  const checked = new Set<string>();
  for (const time of times) {
    const result = checkClockTime(time);
    if (!result.ok) return err('housekeeping.times_invalid');
    checked.add(result.value);
  }
  if (checked.size === 0 || checked.size > MAX_SCHEDULE_TIMES)
    return err('housekeeping.times_invalid');
  return ok([...checked].sort());
}

/** The due instants of a schedule's times on an East Africa day. */
export function scheduledDueTimes(onDate: string, times: readonly string[]): Date[] {
  return times.map((time) => shiftWindow(onDate, time, time).start);
}

/** What a bed waiting for cleaning is waiting for, in words for the bed board. */
export function cleaningWords(task: {
  kind: BedCleaningKind;
  status: HousekeepingTaskStatus;
}): string {
  if (task.kind === 'terminal_clean')
    return task.status === 'in_progress' ? 'terminal clean in progress' : 'terminal clean needed';
  return task.status === 'in_progress' ? 'being cleaned' : 'waiting for housekeeping';
}
